/**
 * Hinge-helper graft — ADR-028 E1's bone, riding the finger-graft machinery as
 * the smaller sibling §4 promised: real rig bones through the same
 * `localBindFromParent` decomposition and the same `parseRigDescriptor`
 * re-gate, no new bone-adding mechanism.
 *
 * THE AXES ARE MEASURED PER CHARACTER, NEVER DECLARED PER FAMILY. The family
 * template declares the RULE — driver/end bone names, the world anterior the
 * elbow bends toward, the helper's share — and this graft measures the
 * concrete hinge/limb axes from the rig's own bind, because the measurement
 * that was supposed to justify a family constant falsified it first: amari's
 * R_Forearm hinge sits at local (0.98, 0, −0.21), the goblin's at (−1, 0, 0).
 * Tripo rolls bones arbitrarily about their long axis within one family
 * ([[generated-rig-axis-soup]] striking inside the family boundary), so a
 * declared axis would be silently wrong on most specimens — the exact failure
 * §7 exists to refuse.
 *
 * The helper sits AT the hinge joint, as a child of the DRIVER'S PARENT (so it
 * does not inherit the bend), wearing the driver's own bind orientation — the
 * evaluator's axis frame is then shared between driver and helper, and the
 * helper's played local rotation is simply `bindLocal · R(hingeAxis, f·θ)`.
 *
 * Names wear the `_corr` class (§6e): one vocabulary, excluded at every
 * signature/classification door, enforced here at the constructor.
 *
 * Depends on: ir/rig-descriptor, ir/priors (template type), math, hands/graft-digits
 * (localBindFromParent). Depended on by: the Document driver's hinge pass.
 */
import { KernelError } from '../ir/errors.js';
import { parseRigDescriptor, type RigBone, type RigDescriptor } from '../ir/rig-descriptor.js';
import type { HingeTemplate } from '../ir/priors.js';
import type { EvidenceMarker } from '../hands/separation-profile.js';
import { localBindFromParent } from '../hands/graft-digits.js';
import { quatInvertUnit, quatRotateVec3 } from '../math/quat.js';
import { vec3Cross, vec3Length, vec3Normalize, vec3Scale, vec3Sub } from '../math/vec3.js';
import type { HingeProfile } from './hinge-correctives.js';

/** The corrective name class — ONE vocabulary, consulted by every door (§6e). */
export const CORRECTIVE_SUFFIX = '_corr';
export function isCorrectiveBone(name: string): boolean {
  return name.endsWith(CORRECTIVE_SUFFIX);
}

export class HingeGraftError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot graft hinge helpers (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/**
 * The template's `anterior` is declared in the CANONICAL FACING FRAME (+Z =
 * where the character faces, +Y up); the import pipeline PROMOTES facing
 * (the committed goblin arrives +Z and is promoted to +X), so the vector is
 * rotated about the up axis into the rig's own measured facing (Codex P1,
 * #103 — a world-constant anterior measures a wrong bend axis on every
 * promoted rig). Returns null for a rig this mapping is not defined on
 * (non-Y-up) — the caller marks and skips, never guesses.
 */
function anteriorInWorld(rig: RigDescriptor, canonical: { x: number; y: number; z: number }): { x: number; y: number; z: number } | null {
  if (rig.upAxis !== 'Y') return null;
  const theta = rig.facing === '+Z' ? 0 : rig.facing === '-Z' ? Math.PI : rig.facing === '+X' ? Math.PI / 2 : -Math.PI / 2;
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return { x: canonical.x * c + canonical.z * s, y: canonical.y, z: -canonical.x * s + canonical.z * c };
}

/** One E2 muscle leaf, resolved: site, frame, and scale made concrete against this rig. */
export interface ResolvedLeaf {
  /** The leaf bone's name (wears the corrective class). */
  readonly name: string;
  /** The bone the leaf rides (proximal segment bone or the driver). */
  readonly parentBone: string;
  /** Site in bind-world space — the belly's center, for the compartment reweight. */
  readonly siteWorld: { readonly x: number; readonly y: number; readonly z: number };
  /**
   * AXIAL half-length of the belly along the segment, world units (template
   * fraction × segment length). The reweight's region is this axial band ∧
   * the anterior half-space — RADIALLY UNBOUNDED, because a radius from the
   * bone line cannot reach the skin of a thick arm (measured: amari's flesh
   * sits ~7cm off a 14.8cm humerus; a 0.28-fraction sphere caught 38 of the
   * belly's ~155 vertices and the swell read zero).
   */
  readonly axialWorld: number;
  /** Share of chain weight taken at the belly center (tapers to 0 at the axial edge). */
  readonly weightShare: number;
  /**
   * The AMPLITUDE SCALE the evaluator's normalized displacement multiplies by
   * — a belly RADIUS, always. At graft (no mesh in the kernel) it is the
   * family's declared `radiusPrior × segment length`; the Document run
   * replaces it with the belly's MEASURED ring radius at bind (adapter
   * measureLeafGirth), because ADR-028 §6 requires amplitude to scale with
   * GIRTH: two arms of equal length and different thickness must not swell
   * by the same millimetres (STAR's BMI lesson, Codex P2 #107 round three).
   * A run that could not measure girth keeps the prior — the same units, so
   * the ramp's calibration holds — and SAYS so (`hinge-leaf-girth-unmeasured`,
   * Codex P2 #107 round four).
   */
  readonly scaleWorld: number;
  /** The segment's bind length, world units — evidence beside the measured scale. */
  readonly segmentWorld: number;
  /** The leaf's bind-LOCAL position in its parent's frame — the driver adds the delta to this. */
  readonly bindLocalPosition: { readonly x: number; readonly y: number; readonly z: number };
  /** The segment's bind-world direction (origin → hinge/end) — the band's axis. */
  readonly axisWorld: { readonly x: number; readonly y: number; readonly z: number };
  /** The displacement direction in bind-world — the compartment's half-space normal. */
  readonly dirWorld: { readonly x: number; readonly y: number; readonly z: number };
}

/** One grafted hinge, resolved against a concrete rig — everything a driver or band-reweight needs. */
export interface ResolvedHinge {
  /** The helper bone's name (wears the corrective class). */
  readonly helper: string;
  /** The driver bone (e.g. R_Forearm) whose bend the helper halves. */
  readonly driver: string;
  /** The driver's parent (the helper's parent too — it must not inherit the bend). */
  readonly parent: string;
  /** The chain's end bone (e.g. R_Hand) — defines the limb axis with the driver. */
  readonly end: string;
  /** Crease-band half-width in world units (template fraction × driver→end length). */
  readonly bandWorld: number;
  /** The evaluator profile with MEASURED axes in the driver's bind-local frame. */
  readonly profile: HingeProfile;
  /** E2 muscle leaves riding this hinge — empty when the template declares none. */
  readonly leaves: readonly ResolvedLeaf[];
}

export interface GraftHingeHelpersResult {
  /** The descriptor with helper bones added as REAL bones (re-gated by the parser). */
  readonly rig: RigDescriptor;
  readonly hinges: readonly ResolvedHinge[];
  /** Geometry degeneracies — hinges SKIPPED with evidence, never silently and never fatally. */
  readonly markers: readonly EvidenceMarker[];
  /**
   * Corrective bones the rig ALREADY wore — an augmented export re-imported
   * (Allen's exports come back through the drop door; the bake harness
   * anchors on one). They are adopted, not re-grafted, and the weight doors
   * must not run again for them: the band and the belly transfers already
   * live in the paint, and a second pass would compound them
   * (helper = 1 − (1 − f)² instead of f). Names, helpers and leaves alike.
   */
  readonly adopted: readonly string[];
}

/**
 * Graft one helper bone per template hinge into the rig, measuring each
 * hinge's axes from the rig's own bind. Two failure classes, deliberately
 * different (§7): a template naming bones the rig lacks, or a colliding /
 * unclassed helper name, is a CALLER bug and refuses loudly; a specimen whose
 * bind GEOMETRY cannot define the hinge (limb collinear with the declared
 * anterior, zero-length limb) is a property of the character, so that hinge
 * is skipped with a marker — the run proceeds, the evidence says why the
 * elbow keeps its macaroni.
 */
export function graftHingeHelpers(rig: RigDescriptor, template: HingeTemplate): GraftHingeHelpersResult {
  const issues: string[] = [];
  const markers: EvidenceMarker[] = [];
  const byName = new Map(rig.bones.map((b) => [b.name, b] as const));
  const newBones: RigBone[] = [];
  const hinges: ResolvedHinge[] = [];
  const adopted: string[] = [];
  /**
   * An existing bone of the corrective class, seated where this graft would
   * seat it (same parent), IS this corrective from an earlier graft — adopt
   * it. Anything else wearing the name is a collision: refused, as before.
   */
  const adoptable = (name: string, parentName: string): RigBone | null => {
    const existing = byName.get(name);
    if (existing === undefined) return null;
    if (!isCorrectiveBone(name)) return null;
    return existing.parent === parentName ? existing : null;
  };

  for (const spec of template.hinges) {
    if (!isCorrectiveBone(spec.helper)) {
      issues.push(`helper "${spec.helper}" does not wear the ${CORRECTIVE_SUFFIX} class — the doors cannot exclude it`);
      continue;
    }
    const driver = byName.get(spec.driver);
    const end = byName.get(spec.end);
    const existingHelper =
      driver !== undefined && driver.parent !== null ? adoptable(spec.helper, driver.parent) : null;
    if (byName.has(spec.helper) && existingHelper === null) {
      issues.push(`helper "${spec.helper}" collides with an existing rig bone`);
      continue;
    }
    if (driver === undefined || end === undefined) {
      issues.push(`hinge "${spec.helper}": rig lacks ${driver === undefined ? `driver "${spec.driver}"` : ''}${driver === undefined && end === undefined ? ' and ' : ''}${end === undefined ? `end "${spec.end}"` : ''}`);
      continue;
    }
    if (driver.parent === null) {
      issues.push(`hinge "${spec.helper}": driver "${spec.driver}" has no parent — a root cannot hinge`);
      continue;
    }
    const parent = byName.get(driver.parent)!;

    // --- measure the axes from THIS rig's bind ---
    const limbVec = vec3Sub(end.worldBind.position, driver.worldBind.position);
    const limbLen = vec3Length(limbVec);
    if (limbLen < 1e-9) {
      markers.push({
        code: 'hinge-axis-degenerate',
        detail: { helper: spec.helper, reason: `"${spec.driver}"→"${spec.end}" has zero length — no limb axis` },
      });
      continue;
    }
    const limbWorld = vec3Scale(limbVec, 1 / limbLen);
    const anteriorWorld = anteriorInWorld(rig, spec.anterior);
    if (anteriorWorld === null) {
      markers.push({
        code: 'hinge-anterior-unresolved',
        detail: { helper: spec.helper, reason: `facing-frame mapping undefined for upAxis "${rig.upAxis}"` },
      });
      continue;
    }
    const hingeRaw = vec3Cross(limbWorld, anteriorWorld);
    if (vec3Length(hingeRaw) < 0.2) {
      markers.push({
        code: 'hinge-axis-degenerate',
        detail: {
          helper: spec.helper,
          reason: `limb axis near-collinear with the declared anterior (|cross| = ${vec3Length(hingeRaw).toFixed(3)})`,
        },
      });
      continue;
    }
    const hingeWorld = vec3Normalize(hingeRaw);
    const invDriverRot = quatInvertUnit(driver.worldBind.rotation);
    const hingeAxisLocal = quatRotateVec3(invDriverRot, hingeWorld);
    const limbAxisLocal = quatRotateVec3(invDriverRot, limbWorld);

    // --- the helper bone: at the joint, in the driver's frame, under the driver's parent ---
    //     An adopted helper keeps the bind it was exported with; the profile's
    //     axes are still MEASURED from this rig (the driver's bind), as always.
    if (existingHelper !== null) {
      adopted.push(spec.helper);
      markers.push({ code: 'hinge-helper-adopted', detail: { helper: spec.helper, parent: parent.name } });
    } else {
      const worldBind: RigBone['worldBind'] = {
        position: driver.worldBind.position,
        rotation: driver.worldBind.rotation,
        scale: driver.worldBind.scale,
      };
      newBones.push({
        name: spec.helper,
        parent: parent.name,
        localBind: localBindFromParent(parent.worldBind, worldBind),
        worldBind,
      });
    }

    // --- E2 leaves: the anatomical frame made concrete against THIS rig ---
    // Each segment carries its own frame: proximal = parent→driver (the
    // humerus), distal = driver→end. "Proximal" points back along that
    // segment toward its origin; "lateral" completes the right-handed triple.
    const leafHelpers: HingeProfile['helpers'][number][] = [];
    const resolvedLeaves: ResolvedLeaf[] = [];
    for (const leaf of spec.leaves ?? []) {
      if (!isCorrectiveBone(leaf.name)) {
        issues.push(`leaf "${leaf.name}" does not wear the ${CORRECTIVE_SUFFIX} class — the doors cannot exclude it`);
        continue;
      }
      const segOrigin = leaf.segment === 'proximal' ? parent : driver;
      if (byName.has(leaf.name) && adoptable(leaf.name, segOrigin.name) === null) {
        issues.push(`leaf "${leaf.name}" collides with an existing rig bone`);
        continue;
      }
      const segEnd = leaf.segment === 'proximal' ? driver : end;
      const segVec = vec3Sub(segEnd.worldBind.position, segOrigin.worldBind.position);
      const segLen = vec3Length(segVec);
      if (segLen < 1e-9) {
        markers.push({
          code: 'hinge-leaf-degenerate',
          detail: { leaf: leaf.name, reason: `${leaf.segment} segment has zero length` },
        });
        continue;
      }
      const segDir = vec3Scale(segVec, 1 / segLen);
      const d = leaf.direction;
      // The lateral axis is built ONLY when the declaration uses it — for a
      // segment parallel to the anterior its cross is zero, and normalizing
      // unconditionally threw and killed the whole document for a component
      // the leaf never asked for (Codex P2, #107).
      let lateralWorld = { x: 0, y: 0, z: 0 };
      if (d.lateral !== 0) {
        const lateralRaw = vec3Cross(segDir, anteriorWorld);
        if (vec3Length(lateralRaw) < 1e-6) {
          markers.push({
            code: 'hinge-leaf-degenerate',
            detail: { leaf: leaf.name, reason: 'lateral axis undefined — segment parallel to the anterior' },
          });
          continue;
        }
        lateralWorld = vec3Normalize(lateralRaw);
      }
      const dirRaw = {
        x: anteriorWorld.x * d.anterior - segDir.x * d.proximal + lateralWorld.x * d.lateral,
        y: anteriorWorld.y * d.anterior - segDir.y * d.proximal + lateralWorld.y * d.lateral,
        z: anteriorWorld.z * d.anterior - segDir.z * d.proximal + lateralWorld.z * d.lateral,
      };
      if (vec3Length(dirRaw) < 1e-6) {
        markers.push({
          code: 'hinge-leaf-degenerate',
          detail: { leaf: leaf.name, reason: 'anatomical components cancel on this geometry — no displacement direction' },
        });
        continue;
      }
      const dirWorld = vec3Normalize(dirRaw);
      // An adopted leaf's SITE is where it actually sits (its exported bind),
      // not the template's declared `along` — a template edited between
      // releases, or an edited export, would otherwise measure girth and
      // build the compartment around a different part of the limb than the
      // seat the driver displaces from (Codex P2, #110).
      const existingLeaf = adoptable(leaf.name, segOrigin.name);
      // The parent's scale is divided into BOTH the leaf's bind decomposition
      // (localBindFromParent) and the stored direction below, so a zero
      // component is checked before either — a distal leaf under a
      // zero-scaled driver used to push a NaN bone that parseRigDescriptor
      // then rejected, killing the whole document where the leaf should have
      // SKIPPED with its marker (Codex P2, #107 round two).
      const ps = segOrigin.worldBind.scale;
      if (Math.abs(ps.x) < 1e-9 || Math.abs(ps.y) < 1e-9 || Math.abs(ps.z) < 1e-9) {
        markers.push({
          code: 'hinge-leaf-degenerate',
          detail: { leaf: leaf.name, reason: `parent "${segOrigin.name}" has a zero scale component` },
        });
        continue;
      }
      const site =
        existingLeaf !== null
          ? existingLeaf.worldBind.position
          : {
              x: segOrigin.worldBind.position.x + segVec.x * leaf.along,
              y: segOrigin.worldBind.position.y + segVec.y * leaf.along,
              z: segOrigin.worldBind.position.z + segVec.z * leaf.along,
            };
      const leafWorldBind: RigBone['worldBind'] = {
        position: site,
        rotation: segOrigin.worldBind.rotation,
        scale: segOrigin.worldBind.scale,
      };
      // An adopted leaf keeps its exported bind-local seat (the driver adds the
      // ramp's delta to it); a fresh one is seated at the declared site.
      const localBind = existingLeaf !== null ? existingLeaf.localBind : localBindFromParent(segOrigin.worldBind, leafWorldBind);
      if (existingLeaf !== null) {
        adopted.push(leaf.name);
        markers.push({ code: 'hinge-leaf-adopted', detail: { leaf: leaf.name, parent: segOrigin.name } });
      } else {
        newBones.push({ name: leaf.name, parent: segOrigin.name, localBind, worldBind: leafWorldBind });
      }
      // The evaluator's leaf direction lives in the leaf PARENT's bind-local
      // frame — the driver adds `direction · ramp(θ) · scaleWorld` to the
      // leaf's bind-local position, all in that one frame. The parent's
      // inverse SCALE is folded in per-axis (Codex P2, #107): local
      // translation is multiplied by the parent's scale on the way back to
      // world, so an uncompensated direction would swell a scaled hierarchy
      // by the scale again (and skew under non-uniform scale). The stored
      // direction is therefore deliberately NON-UNIT; the evaluator applies
      // it as declared.
      const dirRot = quatRotateVec3(quatInvertUnit(segOrigin.worldBind.rotation), dirWorld);
      const dirParentLocal = { x: dirRot.x / ps.x, y: dirRot.y / ps.y, z: dirRot.z / ps.z };
      leafHelpers.push({
        kind: 'leaf',
        name: leaf.name,
        direction: dirParentLocal,
        ramp: { points: leaf.ramp.map((p) => ({ thetaDeg: p.thetaDeg, value: p.value })) },
      });
      resolvedLeaves.push({
        name: leaf.name,
        parentBone: segOrigin.name,
        siteWorld: site,
        axialWorld: leaf.radiusFraction * segLen,
        weightShare: leaf.weightShare,
        scaleWorld: leaf.radiusPrior * segLen,
        segmentWorld: segLen,
        bindLocalPosition: localBind.position,
        axisWorld: segDir,
        dirWorld,
      });
    }
    hinges.push({
      helper: spec.helper,
      driver: spec.driver,
      parent: parent.name,
      end: spec.end,
      bandWorld: spec.bandFraction * limbLen,
      profile: {
        driver: spec.driver,
        hingeAxisLocal,
        limbAxisLocal,
        helpers: [{ kind: 'half-rotation', name: spec.helper, factor: spec.factor }, ...leafHelpers],
        // E3's crease morphs pass through UNRESOLVED, and that asymmetry is the
        // point: a leaf's direction must be resolved into a concrete frame here
        // (anatomical → world → parent-local, with the parent's scale folded
        // in), because a bone displacement is geometry. A morph weight is a
        // scalar on a target the mesh already carries, so the declaration IS
        // the runtime spec — the derived SHAPE is what the character supplies,
        // and it lives in the mesh, not in this profile.
        morphs: (spec.morphs ?? []).map((m) => ({
          name: m.name,
          ramp: { points: m.ramp.map((p) => ({ thetaDeg: p.thetaDeg, value: p.value })) },
        })),
      },
      leaves: resolvedLeaves,
    });
  }
  if (issues.length > 0) throw new HingeGraftError(issues);

  const rigWithHelpers =
    newBones.length === 0
      ? rig
      : parseRigDescriptor({
          ...rig,
          bones: [...rig.bones, ...newBones],
        });
  return { rig: rigWithHelpers, hinges, markers, adopted };
}
